import { openProgramCover, programGalleryReturn, restoreProgramGalleryScroll, finishProgramCover, returningProgramSlug } from '@/utils/programCoverMotion';
import StartupPageTitle from '../../../components/StartupPageTitle';
import { Buildings as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { fileUrl } from '@/api';
import { toast } from 'sonner';
import { Buildings, MagnifyingGlass, CheckCircle, Clock, X, FloppyDisk, ArrowRight, Funnel, RocketLaunch, Wallet, ArrowUpRight } from '@phosphor-icons/react';
import './StartupPrograms.css';
import StartupSubmissionPreview from './StartupSubmissionPreview';
import { stripHtml } from '@/components/RichTextEditor';
import { formatDate, parseTimestamp } from '@/utils/datetime';

const STATUS_META = {
  submitted: { label: 'Application Received', icon: Clock, classes: 'bg-info/10 text-info border-info/30' },
  received: { label: 'Application Received', icon: Clock, classes: 'bg-info/10 text-info border-info/30' },
  under_review: { label: 'Under Review', icon: Clock, classes: 'bg-info/10 text-info border-info/30' },
  accepted: { label: 'Accepted', icon: CheckCircle, classes: 'bg-success/10 text-success border-success/30' },
  rejected: { label: 'Rejected', icon: X, classes: 'bg-danger/10 text-danger border-danger/30' },
  waitlisted: { label: 'Waitlisted', icon: Clock, classes: 'bg-text-muted/10 text-text-muted border-text-muted/30' },
};

// A program may carry an `open_until` auto-close deadline. Turn it into a
// countdown a founder can gauge at a glance; urgency escalates as it nears.
const deadlineInfo = (openUntil) => {
  const d = parseTimestamp(openUntil);
  if (!d) return null;
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return { label: 'Deadline passed', tone: 'danger', expired: true };
  const hours = ms / 3600000;
  if (hours < 24) return { label: `Closes in ${Math.max(1, Math.round(hours))}h`, tone: 'danger' };
  const days = Math.ceil(hours / 24);
  return {
    label: `Closes in ${days} day${days === 1 ? '' : 's'} · ${formatDate(d, { day: 'numeric', month: 'short' })}`,
    short: `closes in ${days}d`,
    tone: days <= 3 ? 'danger' : days <= 14 ? 'warning' : 'neutral',
  };
};

const ProgramMark = ({ program }) => {
  const Icon = program.entity_type === 'incubator' ? RocketLaunch : Wallet;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [program.fund_logo_url]);
  return <div className="program-mark">
    {program.fund_logo_url && !imageFailed
      ? <img src={fileUrl(program.fund_logo_url)} alt="" onError={() => setImageFailed(true)} />
      : <Icon size={24} />}
  </div>;
};

const OpportunityCover = ({cover}) => {
  const [failed,setFailed]=useState(false);
  useEffect(()=>setFailed(false),[cover]);
  return cover&&!failed?<img className="og-cover" src={fileUrl(cover)} alt="" loading="lazy" onError={()=>setFailed(true)}/>:null;
};
const coverStyle=id=>{
  const hash=[...String(id)].reduce((n,c)=>n+c.charCodeAt(0),0);
  return {'--og-aura':['#257e7c','#4f4b8e','#9b6a43','#3e6c85'][hash%4]};
};
const ProgramCard = ({program:p,navigate,ui}) => {
  const deadline=deadlineInfo(p.open_until);
  const target=`/apply/${encodeURIComponent(p.slug)}?source=programs`;
  return <article className="og-card" style={coverStyle(p.id)} data-testid={`discover-card-${p.id}`}>
    <a href={target} aria-label={`View ${p.name}`} onClick={event=>openProgramCover(event,p.slug,navigate,ui)}>
      <OpportunityCover cover={p.cover_image} name={p.name}/><div className="og-shade"/>
      <div className="og-card-top"><div className="og-host"><ProgramMark program={p}/><span>{p.fund_name||'FounderBoard program'}</span></div><span className="og-kind">{p.entity_type==='incubator'?'Incubator':'VC'}</span></div>
      <div className="og-story"><h3>{p.name}</h3>{p.hero_text&&<h4>{stripHtml(p.hero_text)}</h4>}<p>{stripHtml(p.description)||'Meet the people and support behind your next stage of growth.'}</p></div>
      <div className="og-card-bottom"><span className={`og-deadline ${deadline?.tone||'neutral'}`}><Clock size={13}/>{deadline?.label||'Rolling applications'}</span><span className="og-card-action">{p.applied?<><CheckCircle size={14}/>Applied</>:<>View program<ArrowUpRight size={15}/></>}</span></div>
    </a>
  </article>;
};
const OpportunityGallery = ({programs,navigate,ui}) => <section className="og-gallery" aria-label="All programs">
  <div className="og-grid">{programs.map(p=><ProgramCard key={p.id} program={p} navigate={navigate} ui={ui}/>)}</div>
</section>;
const ProgramApplicationCard = ({record:r,draft,navigate,onDiscard}) => {
  const open=r.program_status==='open',meta=STATUS_META[r.status]||STATUS_META.received,StatusIcon=meta.icon;
  const mark={fund_logo_url:r.fund_logo_url,entity_type:r.entity_type};
  if(!draft)return <article className="og-application is-submitted" style={coverStyle(r.program_id||r.id)} data-testid={`applied-row-${r.id}`}>
    <div className="og-application-cover"><OpportunityCover cover={r.program_cover_image}/><div className="og-shade"/><ProgramMark program={mark}/><span className={`og-application-state ${r.status}`} data-testid={`status-${r.id}`}><StatusIcon size={14}/>{meta.label}</span></div>
    <div className="og-application-content"><small>{r.fund_name}</small><h3>{r.program_name}</h3><p>{r.submitted_at ? `Submitted ${formatDate(r.submitted_at)}` : 'Application submitted'}</p>
      <div className="og-application-actions"><a href={`?submission=${encodeURIComponent(r.id)}`} className="program-button og-submission-link" onClick={event=>{if(event.button!==0||event.ctrlKey||event.metaKey||event.shiftKey||event.altKey)return;event.preventDefault();navigate(`?submission=${encodeURIComponent(r.id)}`);}}>View submission<ArrowUpRight size={14}/></a></div>
    </div>
  </article>;
  return <article className="og-application is-draft" style={coverStyle(r.program_id||r.id)} data-testid={`draft-row-${r.id}`}>
    <div className="og-application-cover"><OpportunityCover cover={r.program_cover_image}/><div className="og-shade"/><ProgramMark program={mark}/><span className="og-draft-state"><FloppyDisk size={12}/>Draft</span></div>
    <div className="og-application-content"><small>{r.fund_name}</small><h3>{r.program_name}</h3><p>{r.answered_count}/{r.questions_count} answered · Saved {formatDate(r.updated_at)}</p><progress max={Math.max(1,r.questions_count||0)} value={Math.min(r.answered_count||0,r.questions_count||0)} aria-label="Application progress"/>{open&&<span className="og-draft-deadline" data-testid={`draft-deadline-${r.id}`}>{deadlineInfo(r.program_open_until)?.label||'Rolling applications'}</span>}
      <div className="og-application-actions">{open?<button className="program-button" onClick={()=>navigate(`/apply/${encodeURIComponent(r.program_slug)}?source=programs`)} data-testid={`resume-draft-${r.id}`}>Continue application<ArrowRight size={13}/></button>:<span>Program closed</span>}<button className="og-discard" onClick={()=>onDiscard(r.program_id)} aria-label={`Discard draft for ${r.program_name}`} title="Discard draft" data-testid={`discard-draft-${r.id}`}><X size={16}/></button></div>
    </div>
  </article>;
};

const ProgramLoading = () => <div className="program-grid" role="status" aria-label="Loading programs">
  {Array.from({ length: 4 }, (_, i) => <div className="program-skeleton" key={i}><div /><div /><div /></div>)}
  <span className="sr-only">Loading programs…</span>
</div>;

const StartupPrograms = ({ user }) => {
  const [tab, setTab] = useState(()=>new URLSearchParams(window.location.search).has('submission')?'applied':'discover');
  const [programs, setPrograms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discoverLoading,setDiscoverLoading]=useState(true);
  const latestFetch=useRef(0);
  const [filterType, setFilterType] = useState(()=>programGalleryReturn()?.filterType||'all'); // all | incubator | vc
  const [search, setSearch] = useState(()=>programGalleryReturn()?.search||'');
  const [searchParams,setSearchParams] = useSearchParams();
  const submissionId=searchParams.get('submission');
  const autoApplyHandled = useRef(false);
  const navigate = useNavigate();
  useEffect(()=>{if(submissionId)setTab('applied');},[submissionId]);
  const closeSubmission=()=>{const next=new URLSearchParams(searchParams);next.delete('submission');setSearchParams(next);requestAnimationFrame(()=>document.querySelector(`[data-testid="applied-row-${CSS.escape(submissionId||'')}"] a`)?.focus());};

  useEffect(() => { fetchAll(); return()=>{latestFetch.current++;}; }, []);

  // Deep-link handoff from login/signup: always open the canonical application page.
  // This keeps every startup application on the same canonical form used by public program links.
  useEffect(() => {
    if (loading || autoApplyHandled.current) return;
    const applySlug = searchParams.get('apply') || localStorage.getItem('pending_program_apply_slug');
    if (!applySlug) return;

    autoApplyHandled.current = true;
    localStorage.removeItem('pending_program_apply_slug');
    navigate(`/apply/${encodeURIComponent(applySlug)}`, { replace: true });
  }, [loading, navigate, searchParams]);

  const fetchAll = async () => {
    const request=++latestFetch.current;
    setLoading(true);setDiscoverLoading(true);setLoadErrors([]);
    const load=async(url,setter,discover=false)=>{
      try{const response=await api.get(url);if(latestFetch.current===request)setter(response.data);return response;}
      catch(error){if(latestFetch.current===request)setter([]);throw error;}
      finally{if(discover&&latestFetch.current===request)setDiscoverLoading(false);}
    };
    const results=await Promise.allSettled([load('/startup/programs/discover',setPrograms,true),load('/startup/programs/applications',setApplications),load('/startup/programs/drafts',setDrafts)]);
    if(latestFetch.current===request){setLoadErrors(results.flatMap((result,index)=>result.status==='rejected'?[['programs','applications','drafts'][index]]:[]));setLoading(false);}
  };

  const discardDraft = async (programId) => {
    try {
      await api.delete(`/programs/${programId}/draft`);
      setDrafts((prev) => prev.filter((d) => d.program_id !== programId));
      toast.success('Draft discarded');
    } catch { toast.error('Could not discard draft'); }
  };

  const discoverPrograms = useMemo(() => {
    const appliedIds = new Set(applications.map(a => a.program_id).filter(Boolean));
    const appliedSlugs = new Set(applications.map(a => a.program_slug).filter(Boolean));
    return programs.filter(p => !p.applied && !appliedIds.has(p.id) && !appliedSlugs.has(p.slug));
  }, [programs, applications]);

  const filtered = useMemo(()=>discoverPrograms.filter(p => {
    if (filterType !== 'all' && p.entity_type !== filterType) return false;
    if (search) {
      const s = search.toLowerCase();
      const descText = (p.description || '').replace(/<[^>]*>/g, ' ');
      if (!`${p.name} ${p.fund_name} ${descText}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }),[discoverPrograms,filterType,search]);

  useLayoutEffect(()=>{if(discoverLoading||tab!=='discover')return;restoreProgramGalleryScroll();finishProgramCover('back',returningProgramSlug());},[discoverLoading,tab,filtered]);

  if(submissionId)return <div className="startup-programs"><StartupSubmissionPreview key={submissionId} applicationId={submissionId} onBack={closeSubmission}/></div>;

  return (
    <div className="startup-programs">
      <div className="program-page-heading startup-page-header">
        <StartupPageTitle icon={PageHeadingIcon} data-testid="programs-heading">Programs</StartupPageTitle>
        <p>Apply to incubators and dealflow watchlists. Track your applications in one place.</p>
      </div>

      <div className="program-toolbar">
        <div className="program-tabs" role="tablist" aria-label="Programs views">
          {[{ key: 'discover', label: 'Discover', count: discoverPrograms.length },
            { key: 'applied', label: 'My Applications', count: applications.length + drafts.length }].map(item =>
            <button key={item.key} role="tab" id={`program-tab-${item.key}`} aria-selected={tab === item.key}
              aria-controls={`program-panel-${item.key}`} onClick={() => setTab(item.key)}
              className={tab === item.key ? 'is-active' : ''} data-testid={`tab-${item.key}`}>
              {item.label} <span>{(item.key==='discover'?discoverLoading:loading) ? '–' : item.count}</span>
            </button>)}
        </div>
        {tab === 'discover' && <div className="program-tools">
          <label className="program-search">
            <MagnifyingGlass size={17} />
            <span className="sr-only">Search programs, funds, sectors</span>
            <input type="search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search programs, funds, sectors…" data-testid="search-input" />
          </label>
          <button className={`program-filter-toggle ${filterType !== 'all' ? 'is-active' : ''}`}
            onClick={() => setFiltersOpen(!filtersOpen)} aria-expanded={filtersOpen} aria-controls="program-filters">
            <Funnel size={16} /> Filters {filterType !== 'all' && <span className="program-filter-dot" />}
          </button>
        </div>}
      </div>

      {tab === 'discover' && filtersOpen && <div className="program-filters" id="program-filters">
        <span>Program type</span>
        {[{ k: 'all', l: 'All programs' }, { k: 'incubator', l: 'Incubators' }, { k: 'vc', l: 'VC watchlists' }].map(f =>
          <button key={f.k} onClick={() => setFilterType(f.k)} aria-pressed={filterType === f.k}
            className={filterType === f.k ? 'is-active' : ''} data-testid={`filter-${f.k}`}>{f.l}</button>)}
      </div>}

      {loadErrors.length > 0 && <div className="program-error" role="alert">
        <span>Could not load {loadErrors.join(', ')}. Please try again.</span>
        <button onClick={fetchAll}>Retry</button>
      </div>}

      <section role="tabpanel" id={`program-panel-${tab}`} aria-labelledby={`program-tab-${tab}`}>
        {tab === 'discover' && (discoverLoading ? <ProgramLoading /> :
          filtered.length === 0 ? <div className="program-empty" data-testid="empty-discover">
            <Buildings size={36} weight="duotone" />
            <h3>{search || filterType !== 'all' ? 'No matching programs' : 'No programs to show yet'}</h3>
            <p>{search || filterType !== 'all' ? 'Try a different search or show all program types.' : 'New opportunities will appear here when they open.'}</p>
            {(search || filterType !== 'all') && <button className="program-button" onClick={() => { setSearch(''); setFilterType('all'); }}>Clear filters</button>}
          </div> : <OpportunityGallery programs={filtered} navigate={navigate} ui={{search,filterType}}/>)}

        {tab === 'applied' && (
          loading ? <ProgramLoading /> :
            applications.length === 0 && drafts.length === 0 ? (
              <div className="bg-surface border border-dashed border-border rounded-xl p-12 text-center" data-testid="empty-applied">
                <Buildings size={40} className="text-text-muted mx-auto mb-3" weight="thin" />
                <h3 className="font-heading font-semibold text-text-primary mb-1">No applications yet</h3>
                <p className="text-sm text-text-secondary mb-4">Explore open programs and find your next opportunity.</p>
                <button onClick={() => setTab('discover')} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">Browse Programs</button>
              </div>
            ) : (
              <div className="program-applications space-y-3">
                {drafts.length > 0 && (
                  <>
                    <div className="text-[11px] uppercase tracking-wider text-text-muted font-semibold flex items-center gap-1.5" data-testid="drafts-heading">
                      <FloppyDisk size={12} weight="bold" /> Drafts · finish applying
                    </div>
                    <div className="og-applications-grid">{drafts.map(d=><ProgramApplicationCard key={d.id} record={d} draft navigate={navigate} onDiscard={discardDraft}/>)}</div>
                  </>
                )}
                {applications.length > 0 && <div className="program-applications-heading">Submitted <span>{applications.length}</span></div>}
                <div className="og-applications-grid og-submissions-list">{applications.map(a=><ProgramApplicationCard key={a.id} record={a} navigate={navigate}/>)}</div>
              </div>
            )
        )}
      </section>

    </div>
  );
};

export default StartupPrograms;
